import React from 'react';
import { Colors } from 'library';
import { Chip, ProgressBar, Row, Stack, Text, Title } from 'library/ui';
import { ChevronRightRounded, RadioButtonUncheckedOutlined, CheckCircle } from '@mui/icons-material';
import type { DashboardSummary } from '../types';
import { ProgressItem } from './ProgressItem';
import LOGO from 'assets/images/tandem-blocks-logo.svg';
import { PaymentSetupRow } from './PaymentSetupRow';
import { usePostJobNavigation } from 'parent-portal/jobs/postJob/usePostJobNavigation';
import { PermissionType, useUserContext } from 'UserContext';
import { BasicCard } from './BasicCard';

export function OnboardProgress({ data, refresh }: { data: DashboardSummary; refresh: () => void }) {
    const { goToPostJob } = usePostJobNavigation();
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const is_location_setup = data.centers_details.some((center) => center.progress >= 70);
    const progress = (is_location_setup ? 1 : 0) + (data.payment?.status === 'complete' ? 1 : 0) + 1;

    function onPostJobClick() {
        if (!canPostJobs) {
            return;
        }

        goToPostJob('How It Works');
    }

    const completeIcons = (
        <Row gap="sm">
            <CheckCircle htmlColor={Colors.textSecondary} style={{ height: 20, width: 20 }} />
            <Text size="sm" tone="secondary">
                Complete
            </Text>
        </Row>
    );

    const todoIcons = (
        <Row gap="sm">
            <RadioButtonUncheckedOutlined htmlColor={Colors.textSecondary} style={{ height: 20, width: 20 }} />
            <Text size="sm" tone="secondary">
                To Do
            </Text>
            <ChevronRightRounded htmlColor={Colors.textSecondary} style={{ height: 20, width: 20 }} />
        </Row>
    );

    return (
        <BasicCard loading={false}>
            <Stack gap="md" fullWidth fullHeight>
                <Row justify="between">
                    <Title size="h2">Finish Account Set Up</Title>
                    <Chip bgColor={Colors.secondarySoft} textColor={Colors.secondary}>
                        {progress}/4 completed
                    </Chip>
                </Row>
                <Row>
                    <ProgressBar value={(progress / 4) * 100} />
                </Row>
                <Stack gap="sm">
                    <ProgressItem text="Create an Account" icon={completeIcons} />
                    {data?.payment?.status === 'complete' ? (
                        <ProgressItem text="Add a Payment Method" icon={completeIcons} />
                    ) : (
                        <PaymentSetupRow status={data?.payment?.status} refresh={refresh} />
                    )}
                    <ProgressItem
                        text="Post a Substitute Job"
                        onClick={canPostJobs ? onPostJobClick : undefined}
                        icon={todoIcons}
                    />
                    <ProgressItem
                        text="Add Center Information"
                        to={is_location_setup ? '' : '/manage-centers'}
                        icon={is_location_setup ? completeIcons : todoIcons}
                    />
                    <ProgressItem
                        text="Tandem Will Match You with Staff"
                        icon={<LOGO htmlColor={Colors.primary} style={{ height: 28, width: 28 }} />}
                    />
                </Stack>
            </Stack>
        </BasicCard>
    );
}
